import { useEffect, useState } from 'react';
import { categoryReportApi, slaReport, type CategoryReport, type SlaReport, type SlaRow } from '../api';
import { type FeedbackLocale, type FeedbackTranslate } from '../i18n';
import { Card, DataTable, Notice, TableSkeleton, type Column } from '../../../shell/kit';
import { useCategoryCatalog } from '../catalog';
import { errorText } from '../../../shell/errors';
import { SummaryCard } from './summary';

export function ReportsView({
  t,
  locale,
  siteId,
}: {
  t: FeedbackTranslate;
  locale: FeedbackLocale;
  siteId: string | null;
}) {
  const { categoryName, reasonName } = useCategoryCatalog();
  const [sla, setSla] = useState<SlaReport | null>(null);
  const [categories, setCategories] = useState<CategoryReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setSla(await slaReport());
        setCategories(await categoryReportApi());
      } catch (err) {
        setError(errorText(err).message);
      }
    })();
  }, [locale]);

  if (error) return <Notice tone="error">{error}</Notice>;

  // Скелеты держат ту же раскладку, что и готовый отчёт: страница не
  // перестраивается в момент прихода данных. Строчка «Загрузка…» этого не
  // делала — отчёт возникал рывком и сдвигал всё, на что человек уже смотрел.
  if (!sla || !categories) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Card title={t('feedback.reports.sla', { hours: 24 })}>
          <TableSkeleton rows={3} columns={6} label={t('feedback.common.loading')} />
        </Card>
        <Card title={t('feedback.reports.categories')}>
          <TableSkeleton rows={4} columns={4} label={t('feedback.common.loading')} />
        </Card>
        <div className="grid gap-4 md:grid-cols-2">
          <Card title={t('feedback.reports.scores')}>
            <TableSkeleton rows={3} columns={2} label={t('feedback.common.loading')} />
          </Card>
          <Card title={t('feedback.reports.employees')}>
            <TableSkeleton rows={3} columns={3} label={t('feedback.common.loading')} />
          </Card>
        </div>
      </div>
    );
  }

  const slaColumns: ReadonlyArray<Column<SlaRow>> = [
    {
      key: 'site',
      header: t('feedback.reports.site'),
      render: (r) => r.siteName ?? t('feedback.reports.network'),
    },
    { key: 'total', header: t('feedback.reports.total'), numeric: true, render: (r) => r.total },
    { key: 'closed', header: t('feedback.reports.closed'), numeric: true, render: (r) => r.closed },
    {
      key: 'within',
      header: t('feedback.reports.within'),
      numeric: true,
      render: (r) => r.withinTarget,
    },
    {
      key: 'share',
      header: t('feedback.reports.share'),
      numeric: true,
      render: (r) =>
        r.sharePercent === null ? (
          '—'
        ) : (
          <span
            className={
              r.sharePercent >= 95 ? 'font-semibold text-ok' : 'font-semibold text-warn'
            }
          >
            {r.sharePercent} %
          </span>
        ),
    },
    {
      // Доля от закрытых — другой вопрос и другое число. Держим рядом обе, чтобы
      // разница была видна, а не выяснялась в споре между двумя экранами.
      key: 'ofClosed',
      header: t('feedback.reports.shareOfClosed'),
      numeric: true,
      render: (r) => (r.shareOfClosedPercent === null ? '—' : `${r.shareOfClosedPercent} %`),
    },
    {
      key: 'avg',
      header: t('feedback.reports.avg'),
      numeric: true,
      render: (r) => r.avgResponseMinutes ?? '—',
    },
    {
      key: 'overdue',
      header: t('feedback.reports.openOverdue'),
      numeric: true,
      render: (r) => r.openOverdue,
    },
  ];

  const rows: readonly SlaRow[] = [sla.overall, ...sla.bySite];

  return (
    <div className="space-y-4">
      <SummaryCard t={t} locale={locale} siteId={siteId} />
      <Card title={t('feedback.reports.sla', { hours: sla.targetHours })}>
        {/* Формула на экране: рядом на вкладке «Отток» она написана явно,
            а здесь её отсутствие гарантировало спор «как ты считал». */}
        <p className="mb-1 text-xs text-ink-muted">
          {t('feedback.reports.formula', { formula: sla.formula })}
        </p>
        <p className="mb-3 text-xs text-ink-muted">{t('feedback.reports.target')}</p>
        <DataTable
          rows={rows}
          columns={slaColumns}
          empty={t('feedback.common.nothing')}
          rowKey={(r) => r.siteId ?? 'network'}
        />
      </Card>

      <Card title={t('feedback.reports.categories')}>
        <div className="mb-2 flex flex-wrap gap-4 text-xs text-ink-muted">
          <span>{t('feedback.reports.unconfirmed', { n: categories.unconfirmed })}</span>
          <span>{t('feedback.reports.unlinked', { n: categories.unlinkedVisits })}</span>
        </div>
        <DataTable
          rows={categories.categories}
          columns={[
            {
              key: 'c',
              header: t('feedback.queue.category'),
              render: (r) => categoryName(r.category),
            },
            {
              key: 'r',
              header: t('feedback.reports.reason'),
              render: (r) => reasonName(r.reasonCode),
            },
            { key: 'n', header: t('feedback.reports.count'), numeric: true, render: (r) => r.total },
            {
              key: 'avg',
              header: t('feedback.reports.avgScore'),
              numeric: true,
              render: (r) => r.avgScore ?? '—',
            },
          ]}
          empty={t('feedback.common.nothing')}
          rowKey={(r) => `${r.category ?? 'none'}:${r.reasonCode ?? 'none'}`}
        />
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card title={t('feedback.reports.scores')}>
          <DataTable
            rows={categories.scores}
            columns={[
              { key: 's', header: t('feedback.queue.score'), render: (r) => r.score },
              { key: 'n', header: t('feedback.reports.count'), render: (r) => r.total },
            ]}
            empty={t('feedback.common.nothing')}
            rowKey={(r) => String(r.score)}
          />
        </Card>

        <Card title={t('feedback.reports.employees')}>
          <DataTable
            rows={categories.employees}
            columns={[
              {
                key: 'e',
                header: t('feedback.reports.employee'),
                render: (r) => r.employeeName ?? r.employeeId,
              },
              { key: 'n', header: t('feedback.reports.count'), render: (r) => r.total },
              { key: 'avg', header: t('feedback.reports.avgScore'), render: (r) => r.avgScore ?? '—' },
              { key: 'low', header: t('feedback.reports.lowScore'), render: (r) => r.lowScore },
            ]}
            empty={t('feedback.common.nothing')}
            rowKey={(r) => r.employeeId}
          />
        </Card>
      </div>
    </div>
  );
}
